import React from "react";

const WORKSPACES = [
  {
    id: "project-plan",
    label: {
      en: "Project Overview",
      fr: "Vue d’ensemble",
    },
    description: {
      en:
        "Define the screenplay’s identity, format, audience and core premise.",
      fr:
        "Définissez l’identité, le format, le public et la prémisse du scénario.",
    },
  },
  {
    id: "story-map",
    label: {
      en: "Story Map",
      fr: "Story Map",
    },
    description: {
      en:
        "Arrange the important story moments in the order the story unfolds.",
      fr:
        "Organisez les moments importants dans l’ordre où l’histoire se déroule.",
    },
  },
  {
    id: "characters-world",
    label: {
      en: "Characters & World",
      fr: "Personnages et univers",
    },
    description: {
      en:
        "Find and manage characters, locations, relationships and continuity rules in one place.",
      fr:
        "Trouvez et gérez les personnages, lieux, relations et règles de continuité au même endroit.",
    },
  },
  {
    id: "script-workspace",
    label: {
      en: "Script Workspace",
      fr: "Script Workspace",
    },
    description: {
      en:
        "Write scene by scene, move nearby with the Scene strip, or find any Scene directly.",
      fr:
        "Écrivez scène par scène, naviguez localement ou trouvez directement n’importe quelle scène.",
    },
  },
  {
    id: "proposal-round",
    label: {
      en: "Proposal Round",
      fr: "Proposal Round",
    },
    description: {
      en:
        "Invite and submit alternatives against the frozen screenplay context.",
      fr:
        "Invitez et soumettez des alternatives à partir du scénario gelé.",
    },
  },
  {
    id: "review-and-decide",
    label: {
      en: "Review and Decide",
      fr: "Examiner et décider",
    },
    description: {
      en:
        "Owner review of submitted proposals before any Canon change.",
      fr:
        "Examen par le propriétaire avant toute modification du Canon.",
    },
  },
];

export default function ScreenplayWorkspaceNavigator({
  currentWorkspace = "project-plan",
  onNavigate,
  scriptWorkspaceEnabled = true,
  collaborationWorkspaceEnabled = true,
  reviewWorkspaceEnabled = true,
  isFR = false,
}) {
  const language =
    isFR ? "fr" : "en";

  return (
    <nav
      className="ns-card"
      aria-label={
        isFR
          ? "Espaces du Screenplay Studio"
          : "Screenplay Studio workspaces"
      }
      style={{
        padding: 18,
        borderRadius: 20,
        border:
          "1.5px solid rgba(220,38,38,0.20)",
        marginBottom: 18,
      }}
    >
      <div
        style={{
          marginBottom: 13,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 900,
            letterSpacing:
              "0.1em",
            textTransform:
              "uppercase",
            color: "#dc2626",
          }}
        >
          SCREENPLAY STUDIO
        </div>

        <h3
          className="ns-card-title"
          style={{
            margin: "5px 0 5px",
          }}
        >
          {isFR
            ? "Choisissez votre espace de travail"
            : "Choose your workspace"}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Passez d’un espace à l’autre sans quitter ce projet."
            : "Move between work areas without leaving this project."}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
        }}
      >
        {WORKSPACES.map(
          (workspace) => {
            const active =
              currentWorkspace ===
              workspace.id;

            const scriptUnavailable =
              workspace.id ===
                "script-workspace" &&
              !scriptWorkspaceEnabled;

            const collaborationUnavailable =
              (
                workspace.id ===
                  "proposal-round" ||
                workspace.id ===
                  "review-and-decide"
              ) &&
              !collaborationWorkspaceEnabled;

            const reviewUnavailable =
              workspace.id ===
                "review-and-decide" &&
              !reviewWorkspaceEnabled;

            const unavailable =
              scriptUnavailable ||
              collaborationUnavailable ||
              reviewUnavailable;

            let unavailableText =
              "";

            if (
              scriptUnavailable
            ) {
              unavailableText =
                isFR
                  ? "Indisponible aux collaborateurs dans cette version."
                  : "Unavailable to collaborators in this version.";
            } else if (
              collaborationUnavailable
            ) {
              unavailableText =
                isFR
                  ? "Disponible en mode Collaboratif ou Groupe."
                  : "Available in Collaborative or Group Writing modes.";
            } else if (
              reviewUnavailable
            ) {
              unavailableText =
                isFR
                  ? "La décision finale appartient au propriétaire."
                  : "Final decisions belong to the project Owner.";
            }

            return (
              <button
                key={workspace.id}
                type="button"
                className={
                  active
                    ? "ns-button ns-button-primary"
                    : "ns-button ns-button-outline"
                }
                aria-current={
                  active
                    ? "page"
                    : undefined
                }
                disabled={
                  unavailable
                }
                onClick={() => {
                  if (unavailable) {
                    return;
                  }

                  onNavigate?.(
                    workspace.id
                  );
                }}
                style={{
                  textAlign: "left",
                  justifyContent:
                    "flex-start",
                  alignItems:
                    "flex-start",
                  minHeight: 96,
                  padding:
                    "16px 17px",
                  whiteSpace:
                    "normal",
                  borderRadius: 12,
                  width: "100%",
                  boxShadow:
                    active
                      ? "0 4px 12px rgba(15,23,42,0.10)"
                      : "0 1px 3px rgba(15,23,42,0.05)",
                }}
              >
                <span>
                  <span
                    style={{
                      display: "block",
                      fontWeight: 900,
                      marginBottom: 4,
                    }}
                  >
                    {
                      workspace.label[
                        language
                      ]
                    }
                  </span>

                  <span
                    style={{
                      display: "block",
                      fontSize: 12,
                      lineHeight: 1.4,
                      opacity: active
                        ? 0.92
                        : 0.78,
                    }}
                  >
                    {unavailable
                      ? unavailableText
                      : workspace.description[
                          language
                        ]}
                  </span>
                </span>
              </button>
            );
          }
        )}
      </div>
    </nav>
  );
}
